// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// ASP #28 — the Lab Tracker's next action in the person's own words. The derived next action (Call
// patient, Retry patient 10/15 …) stays visible underneath; what staff type is saved per therapy on
// leaving the field or on Enter (mig 436, audited). Clearing the text clears it.

import { useEffect, useRef, useState } from 'react'
import { friendlyError } from '../../api/errors'
import { useSetLabNextAction } from '../../hooks/queries/carerxClinical'

const MAX = 300   // CareRxLabTrackerFunctions.MaxNextActionLength

export default function LabNextActionCell({ therapyId, auto, text, by, onSaved }: {
  therapyId: string
  /** The derived next action. */
  auto: string
  text?: string | null
  by?: string | null
  onSaved: () => void
}) {
  const save = useSetLabNextAction()
  const saved = text ?? ''
  const [draft, setDraft] = useState(saved)
  const [err, setErr] = useState<string | null>(null)
  // The text on its way to the server (review 2026-10-02, S7): Enter saves, and disabling the field
  // while it saves blurs it — which saved the same text a second time (two PUTs, two audit rows).
  const sent = useRef<string | null>(null)
  useEffect(() => { sent.current = null; setDraft(saved) }, [saved])
  const commit = () => {
    const next = draft.trim()
    if (next === saved || next === sent.current) return
    sent.current = next
    setErr(null)
    save.mutate({ therapyId, text: next || null }, {
      onSuccess: onSaved,
      onError: e => { sent.current = null; setErr(friendlyError(e)) },
    })
  }
  return (
    <div style={{ minWidth: 200 }}>
      <input aria-label="Next action" placeholder="Type the next action…" maxLength={MAX} value={draft}
        disabled={save.isPending}
        onChange={e => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit() } }}
        title={saved && by ? `Set by ${by}` : undefined}
        style={{ width: '100%', fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--color-border)',
                 fontWeight: saved ? 600 : 400, background: 'var(--color-surface)', color: 'var(--color-text)', boxSizing: 'border-box' }} />
      <div style={{ fontSize: 10.5, color: 'var(--color-text-mute)', marginTop: 2 }}>auto: {auto}</div>
      {err && <div role="alert" style={{ fontSize: 11, color: '#b23a48' }}>{err}</div>}
    </div>
  )
}
